import {
  AfterContentInit,
  ChangeDetectionStrategy,
  Component,
  ContentChildren,
  forwardRef,
  HostBinding,
  Input,
  OnInit,
  QueryList,
} from '@angular/core';
import { BehaviorSubject, combineLatest } from 'rxjs';
import { debounceTime, startWith, tap } from 'rxjs/operators';
import { GasRange } from '../gas-range.namespace';

import { GasForm } from '../../../forms/gas-form.namespace';
import { GasDatepickerInputComponent } from '../../../forms/gas-datepicker-input/gas-datepicker-input.component';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';

@UntilDestroy()
@Component({
  selector: 'gas-datepicker-range',
  template: '<ng-content></ng-content>',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class GasDatepickerRangeComponent implements OnInit, AfterContentInit {
  /** Нижняя граница диапазона допустимых значений */
  @Input()
  public min: Date | string;

  /** Верхняя граница диапазона допустимых значений */
  @Input()
  public max: Date | string;

  @Input()
  public error: string;

  @HostBinding('class.gas-range')
  public rangeClass = true;

  @ContentChildren(forwardRef(() => GasDatepickerInputComponent), { descendants: true })
  public inputs: QueryList<GasDatepickerInputComponent>;

  private get firstInputComponent(): GasDatepickerInputComponent {
    return this.inputs.first;
  }

  private get secondInputComponent(): GasDatepickerInputComponent {
    return this.inputs.last;
  }

  /** "true" если параметр "from" больше "to" */
  private parametersConsistency = false;

  private smallestValue: number;

  private largestValue: number;

  private lastModifiedInputControl$ = new BehaviorSubject<GasDatepickerInputComponent>(null);

  private hasErrorFirstInputComponent = false;

  private hasErrorSecondInputComponent = false;

  public ngOnInit(): void {
    this.smallestValue = GasRange.calcSmallestDateValue(this.min);
    this.largestValue = GasRange.calcLargestDateValue(this.max);

    /** Проверяем, что параметр "to" больше чем "from"  */
    if (this.smallestValue < this.largestValue) {
      this.parametersConsistency = true;
    }
  }

  public ngAfterContentInit(): void {
    if (this.parametersConsistency) {
      const firstControlValue = this.firstInputComponent.form.valueChanges.pipe(
        debounceTime(300),
        startWith(null),
        /**
         * Следим, какой инпут-контрол из range был изменён последним,
         * чтобы уставоваить ему ошибку, в случае необходимости.
         */
        tap(() => this.lastModifiedInputControl$.next(this.firstInputComponent)),
      );

      const secondControlValue = this.secondInputComponent.form.valueChanges.pipe(
        debounceTime(300),
        startWith(null),
        /**
         * Следим, какой инпут-контрол из range был изменён последним,
         * чтобы уставоваить ему ошибку, в случае необходимости.
         */
        tap(() => this.lastModifiedInputControl$.next(this.secondInputComponent)),
      );

      combineLatest([
        firstControlValue,
        secondControlValue,
      ]).pipe(
        untilDestroyed(this),
      ).subscribe(([first, second]: [Date | string, Date | string]) => {
        /**
         * Если задано начало промежутка и значение не пренадлежит диапазону допустимых значение
         * устанавливаем ошибку первому инпут-контролу
         */

        const firstDate: GasRange.DateParams = { value: first, mask: this.firstInputComponent.inputMask };
        const secondDate: GasRange.DateParams = { value: second, mask: this.secondInputComponent.inputMask };

        if (!GasRange.checkDateInputsValue(firstDate, secondDate)) {
          return;
        }

        if (first && !GasRange.checkBoundaryValues(
          this.smallestValue,
          this.largestValue,
          GasRange.convertDatestringToNumber(first))
        ) {
          this.setInputError(this.firstInputComponent);
          this.hasErrorFirstInputComponent = true;
        } else {
          this.hasErrorFirstInputComponent = false;
        }

        /**
         * Если задан конец промежутка и значение не пренадлежит диапазону допустимых значение
         * устанавливаем ошибку второму инпут-контролу
         */
        if (second && !GasRange.checkBoundaryValues(
          this.smallestValue,
          this.largestValue,
          GasRange.convertDatestringToNumber(second))
        ) {
          this.setInputError(this.secondInputComponent);
          this.hasErrorSecondInputComponent = true;
        } else {
          this.hasErrorSecondInputComponent = false;
        }

        /**
         * Если заданы начало и конец промежутка и значение первое значение больше второго
         * устанавливаем ошибку инпут-контролу, который был изменён последним
         */
        if (first && second && !GasRange.compareDateValues(first, second)) {
          const failedInputControl = this.lastModifiedInputControl$.getValue();
          this.setInputError(failedInputControl);

          /** Отключаем ошибки для обоих инпут-контролов */
        } else {
          if (!this.hasErrorFirstInputComponent) {
            this.disableInputError(this.firstInputComponent);
          }

          if (!this.hasErrorSecondInputComponent) {
            this.disableInputError(this.secondInputComponent);
          }
        }
      });
    }
  }

  /**
   * @description
   * При добавлении/удалении ошибки делаем по две операции, так как где-то мы работаем с внутренней формой
   * где-то работаем напрямую с конроллом
   */
  private setInputError(input: GasDatepickerInputComponent): void {
    input.state = GasForm.InputState.Error;
    input.error = this.error ? this.error : 'Недопустимое значение';
    //eslint-disable-next-line @typescript-eslint/naming-convention
    input.form.setErrors({ INPUT_VALUE_ERROR: input.error }, { emitEvent: false });
    //eslint-disable-next-line @typescript-eslint/naming-convention
    input.ngControl?.control?.setErrors({ INPUT_VALUE_ERROR: input.error }, { emitEvent: false });
  }

  private disableInputError(input: GasDatepickerInputComponent): void {
    input.state = GasForm.InputState.Default;
    input.error = '';
    //eslint-disable-next-line @typescript-eslint/naming-convention
    input.form.setErrors({ INPUT_VALUE_ERROR: null }, { emitEvent: false });
    //eslint-disable-next-line @typescript-eslint/naming-convention
    input.ngControl?.control?.setErrors({ INPUT_VALUE_ERROR: null }, { emitEvent: false });
    input.form.updateValueAndValidity({ emitEvent: false });
  }
}
